"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
export default function Risk() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const capabilities = [{
    value: "hazard-modeling",
    title: "Hazard Modeling & Simulation",
    body: "Our engineering team utilizes advanced computational fluid dynamics to model fire propagation and smoke movement within complex industrial environments. By simulating worst-case scenarios, we provide the data necessary to harden infrastructure against catastrophic loss and ensure operational continuity.",
    specs: ["Computational fluid dynamics (CFD) modeling", "Structural integrity analysis under thermal stress", "Predictive failure modeling for hazardous zones"]
  }, {
    value: "compliance",
    title: "Regulatory Compliance & Standards",
    body: "Bedrock Safety Engineering ensures your facilities meet and exceed international fire safety codes. We bridge the gap between complex regulatory requirements and practical engineering solutions, providing clear documentation for insurance underwriters and local authorities.",
    specs: ["NFPA and international code compliance audits", "Insurance-grade risk assessment reporting", "Permitting and regulatory liaison services"]
  }, {
    value: "suppression",
    title: "Suppression System Design",
    body: "We design bespoke fire suppression systems tailored to the specific chemical and physical hazards of your facility. From high-expansion foam systems to specialized gas suppression, our designs are engineered for reliability in the most demanding industrial conditions.",
    specs: ["Custom suppression system engineering", "Hazard-specific agent selection and integration", "System performance verification and testing"]
  }, {
    value: "resilience",
    title: "Operational Resilience Planning",
    body: "Beyond immediate fire protection, we develop comprehensive resilience strategies that protect your assets and personnel. Our approach integrates physical engineering with emergency response planning to minimize downtime and safeguard your long-term industrial investments.",
    specs: ["Emergency response and evacuation planning", "Business continuity and recovery strategies", "Asset protection and loss mitigation protocols"]
  }];
  return <section data-section-id="20281" id="strategic-risk-management" className="bg-primary py-24 md:py-36">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-[520ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default text-xs font-bold uppercase tracking-[0.15em] text-white/60">
                Risk Management
              </p>
              <div className="mt-5 h-px w-full bg-white/15" />
              <h2 className="mt-8 font-serif font-normal text-white leading-[1.1]" style={{
              fontSize: "clamp(2.125rem, 3.6vw, 3rem)",
              letterSpacing: "-0.018em"
            }}>
                Engineering <em className="italic">absolute</em> resilience
              </h2>
              <p className="mt-6 font-default text-white/75 leading-[1.6] text-[clamp(1.0625rem,1.15vw,1.1875rem)]">
                Bedrock Safety Engineering provides a systematic approach to fire protection and risk management. We fuse rigorous engineering analysis with deep industry expertise to secure your assets, satisfy underwriters, and ensure the safety of your industrial operations.
              </p>
              <div className="mt-8">
                <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-default text-sm font-bold text-white transition-colors duration-[160ms] hover:text-accent-foreground">
                  <span className="underline underline-offset-[6px] decoration-1">
                    View our engineering services
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-[180ms] group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div ref={rightRef} className={`rounded-lg bg-white/5 transition-all duration-[520ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Accordion type="single" collapsible defaultValue="hazard-modeling" className="w-full px-6 md:px-10">
                {capabilities.map((cap, i) => <AccordionItem key={cap.value} value={cap.value} data-index={i} className="border-b border-white/10 last:border-b-0">
                    <AccordionTrigger className="group py-6 hover:no-underline [&>svg]:hidden text-left">
                      <span className="font-default text-lg md:text-xl font-semibold text-white leading-[1.35] tracking-[-0.005em]">
                        {cap.title}
                      </span>
                      <span aria-hidden="true" className="ml-4 flex size-4 shrink-0 items-center justify-center text-white/70 transition-transform duration-[240ms] ease-out group-data-[state=open]:rotate-45">
                        <span className="relative block size-4">
                          <span className="absolute left-1/2 top-1/2 h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-current" />
                          <span className="absolute left-1/2 top-1/2 h-4 w-px -translate-x-1/2 -translate-y-1/2 bg-current" />
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-7 pt-1">
                      <p className="font-default text-white/70 leading-[1.6] text-base">
                        {cap.body}
                      </p>
                      <ul className="mt-6 space-y-2.5">
                        {[cap.specs[0], cap.specs[1], cap.specs[2]].map((spec, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-default text-[0.8125rem] font-medium tracking-[0.01em] text-white/55 leading-[1.4]">
                              <span className="mt-[0.55rem] inline-block h-px w-3 shrink-0 bg-accent-foreground/70" />
                              <span>{spec}</span>
                            </li>)}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}